/* eslint-disable */
/**
 * ============================================================================
 * Flowchart — FigJam-style block diagrams (pure SVG, no deps)
 * ============================================================================
 *
 * Renders user journeys and IA boards as SVG block diagrams. Pairs with the
 * existing text/list views via a toggle inside the wizard.
 *
 * Two layouts:
 *   • "linear"  — sequenced nodes left→right, wrapping to a new row when wide,
 *                 connected by curved arrows. Use for journey FLOWS.
 *   • "board"   — labeled swimlane containers, each holding a row of nodes,
 *                 no connectors. Use for IA groups → screens.
 *
 * Output is real SVG and can be downloaded as a .svg file (also valid XML).
 *
 * USAGE
 * -----
 *   import { Flowchart } from "../../shared/Flowchart";
 *
 *   <Flowchart
 *     dark={dark}
 *     mode="linear"
 *     title="Merchant onboarding"
 *     accent={T.brand}
 *     groups={[
 *       { id: "g", label: "", items: [
 *         { id: "register", label: "Register", sublabel: "PublicShell" },
 *         { id: "verify",   label: "Verify",   sublabel: "PublicShell" },
 *         { id: "hub",      label: "Create hub", sublabel: "AppShell" },
 *       ]}
 *     ]}
 *   />
 *
 *   // Board mode (IA):
 *   <Flowchart
 *     dark={dark}
 *     mode="board"
 *     title="ZiniHub Admin IA"
 *     groups={[
 *       { id: "core", label: "Core", color: T.brand, items: [
 *         { id: "dash", label: "Dashboard" },
 *         { id: "hub",  label: "My Hub" },
 *       ]},
 *       { id: "settings", label: "Settings", color: T.teal, items: [...] }
 *     ]}
 *   />
 * ============================================================================
 */

import { useMemo, useRef } from "react";
import { T, useTheme } from "../tokens";

const NODE_W = 168;
const NODE_H = 64;
const GAP_X  = 36;
const GAP_Y  = 56;
const PAD    = 24;
const SWIMLANE_PAD = 16;
const SWIMLANE_LABEL_W = 132;

// Wrap items into rows that fit `maxW` pixels wide
function wrapRows(items, maxW) {
  const cols = Math.max(1, Math.floor((maxW - PAD * 2 + GAP_X) / (NODE_W + GAP_X)));
  const rows = [];
  for (let i = 0; i < items.length; i += cols) rows.push(items.slice(i, i + cols));
  return { rows, cols };
}

function NodeRect({ x, y, item, accent, th, dark }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        width={NODE_W}
        height={NODE_H}
        rx={10}
        ry={10}
        fill={th.surface}
        stroke={accent || th.borderMd}
        strokeWidth={1.5}
      />
      <rect width={4} height={NODE_H} rx={2} ry={2} fill={accent || T.brand} />
      <text
        x={14}
        y={item.sublabel ? 26 : 36}
        fontSize={12}
        fontWeight={600}
        fill={th.text}
        fontFamily="Inter, system-ui, sans-serif"
      >
        {truncate(item.label, 22)}
      </text>
      {item.sublabel && (
        <text
          x={14}
          y={44}
          fontSize={10}
          fill={th.textMuted}
          fontFamily="Inter, system-ui, sans-serif"
        >
          {truncate(item.sublabel, 26)}
        </text>
      )}
      {item.badge && (
        <g transform={`translate(${NODE_W - 10}, 10)`}>
          <rect
            x={-44}
            y={0}
            width={40}
            height={14}
            rx={3}
            ry={3}
            fill={(accent || T.brand) + "22"}
            stroke={(accent || T.brand) + "55"}
          />
          <text
            x={-24}
            y={10}
            fontSize={8}
            fontWeight={600}
            textAnchor="middle"
            fill={accent || T.brand}
            fontFamily="Inter, system-ui, sans-serif"
          >
            {truncate(item.badge, 6)}
          </text>
        </g>
      )}
    </g>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function Arrow({ from, to, color, curve = "h" }) {
  // from / to are { x, y } center-edge points
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  let d;
  if (curve === "h") {
    const cx = from.x + dx / 2;
    d = `M ${from.x} ${from.y} C ${cx} ${from.y}, ${cx} ${to.y}, ${to.x} ${to.y}`;
  } else {
    // Wrap-around: dip below current row, loop to next row's left
    const midY = from.y + 30;
    d = `M ${from.x} ${from.y} C ${from.x + 30} ${midY}, ${to.x - 30} ${midY}, ${to.x} ${to.y}`;
  }
  return (
    <path
      d={d}
      stroke={color}
      strokeWidth={1.5}
      fill="none"
      markerEnd="url(#arrowhead)"
      strokeLinecap="round"
    />
  );
}

function defs(accent) {
  return (
    <defs>
      <marker
        id="arrowhead"
        viewBox="0 0 10 10"
        refX={9}
        refY={5}
        markerWidth={6}
        markerHeight={6}
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={accent} />
      </marker>
    </defs>
  );
}

function buildLinear(groups, accent, th, dark, width) {
  const items = groups.flatMap((g) => g.items);
  const { rows } = wrapRows(items, width);
  const rowH = NODE_H + GAP_Y;
  const height = PAD * 2 + rows.length * rowH - GAP_Y;
  const nodes = [];
  const arrows = [];
  let flatIndex = 0;
  rows.forEach((row, rIdx) => {
    row.forEach((item, cIdx) => {
      const x = PAD + cIdx * (NODE_W + GAP_X);
      const y = PAD + rIdx * rowH;
      nodes.push({ item, x, y, flatIndex });
      // arrow from previous node
      if (flatIndex > 0) {
        const prev = nodes[flatIndex - 1];
        const prevRow = Math.floor((prev.y - PAD) / rowH);
        if (prevRow === rIdx) {
          arrows.push({
            from: { x: prev.x + NODE_W, y: prev.y + NODE_H / 2 },
            to:   { x: x, y: y + NODE_H / 2 },
            curve: "h",
          });
        } else {
          // wrap: prev was last in its row, this is first in next row
          arrows.push({
            from: { x: prev.x + NODE_W, y: prev.y + NODE_H / 2 },
            to:   { x: x, y: y + NODE_H / 2 },
            curve: "wrap",
          });
        }
      }
      flatIndex++;
    });
  });
  return { width, height, nodes, arrows };
}

function buildBoard(groups, th, dark, width) {
  const lanes = groups.map((g) => {
    const innerW = width - SWIMLANE_LABEL_W - PAD * 2 - SWIMLANE_PAD * 2;
    const { rows } = wrapRows(g.items, innerW + PAD * 2);
    return { group: g, rows };
  });
  const laneH = (lane) =>
    SWIMLANE_PAD * 2 + lane.rows.length * NODE_H + (lane.rows.length - 1) * GAP_Y;
  let cursorY = PAD;
  const placed = lanes.map((lane) => {
    const y = cursorY;
    const h = laneH(lane);
    cursorY += h + GAP_Y / 2;
    return { ...lane, y, h };
  });
  const height = cursorY + PAD - GAP_Y / 2;
  return { width, height, lanes: placed };
}

/**
 * @param {object} props
 * @param {boolean} props.dark
 * @param {"linear"|"board"} [props.mode="linear"]
 * @param {string} [props.title]
 * @param {string} [props.accent]            - default node accent (T.brand)
 * @param {Array<{id:string,label:string,color?:string,items:Array<{id:string,label:string,sublabel?:string,badge?:string,accent?:string}>}>} props.groups
 * @param {number} [props.width=920]
 */
export function Flowchart({ dark, mode = "linear", title, accent, groups = [], width = 920 }) {
  const th = useTheme(dark);
  const svgRef = useRef(null);
  const acc = accent || T.brand;

  const layout = useMemo(() => {
    if (mode === "board") return buildBoard(groups, th, dark, width);
    return buildLinear(groups, acc, th, dark, width);
  }, [mode, groups, width, dark, acc]);

  const handleDownload = (fmt = "svg") => {
    const svg = svgRef.current;
    if (!svg) return;
    const serializer = new XMLSerializer();
    let str = serializer.serializeToString(svg);
    if (!str.startsWith("<?xml")) str = '<?xml version="1.0" encoding="UTF-8"?>\n' + str;
    const blob = new Blob([str], { type: fmt === "xml" ? "application/xml" : "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "flowchart").replace(/\s+/g, "-").toLowerCase()}.${fmt}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        background: th.surface,
        border: `1px solid ${th.border}`,
        borderRadius: T.rlg,
        padding: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {title && <div style={{ fontSize: 12, fontWeight: 600, color: th.text }}>{title}</div>}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => handleDownload("svg")}
          style={dlBtn(th)}
          title="Download as SVG"
        >
          ↓ SVG
        </button>
        <button
          onClick={() => handleDownload("xml")}
          style={dlBtn(th)}
          title="Download as XML"
        >
          ↓ XML
        </button>
      </div>
      <div style={{ overflow: "auto" }}>
        <svg
          ref={svgRef}
          xmlns="http://www.w3.org/2000/svg"
          width={layout.width}
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          style={{ background: dark ? T.dCanvas : T.lMuted, borderRadius: T.rmd, display: "block" }}
        >
          {defs(acc)}
          {mode === "linear" && (
            <>
              {layout.arrows.map((a, i) => (
                <Arrow key={`a${i}`} {...a} color={th.borderMd === T.dBorderMd ? "#94a3b8" : "#475569"} />
              ))}
              {layout.nodes.map((n, i) => (
                <NodeRect
                  key={n.item.id || i}
                  x={n.x}
                  y={n.y}
                  item={n.item}
                  accent={n.item.accent || acc}
                  th={th}
                  dark={dark}
                />
              ))}
            </>
          )}
          {mode === "board" &&
            layout.lanes.map((lane, li) => {
              const color = lane.group.color || acc;
              return (
                <g key={lane.group.id || li}>
                  {/* lane container */}
                  <rect
                    x={PAD}
                    y={lane.y}
                    width={layout.width - PAD * 2}
                    height={lane.h}
                    rx={12}
                    ry={12}
                    fill={dark ? T.dMuted : T.lMuted}
                    stroke={color + "44"}
                    strokeWidth={1}
                  />
                  <rect
                    x={PAD}
                    y={lane.y}
                    width={6}
                    height={lane.h}
                    rx={3}
                    ry={3}
                    fill={color}
                  />
                  {/* lane label */}
                  <text
                    x={PAD + 18}
                    y={lane.y + 22}
                    fontSize={11}
                    fontWeight={700}
                    fill={color}
                    fontFamily="Inter, system-ui, sans-serif"
                    style={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    {lane.group.label}
                  </text>
                  <text
                    x={PAD + 18}
                    y={lane.y + 36}
                    fontSize={9}
                    fill={th.textMuted}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    {lane.group.items.length} screens
                  </text>
                  {/* nodes */}
                  {lane.rows.map((row, rIdx) =>
                    row.map((item, cIdx) => {
                      const x = PAD + SWIMLANE_LABEL_W + cIdx * (NODE_W + GAP_X);
                      const y = lane.y + SWIMLANE_PAD + rIdx * (NODE_H + GAP_Y);
                      return (
                        <NodeRect
                          key={item.id || `${li}-${rIdx}-${cIdx}`}
                          x={x}
                          y={y}
                          item={item}
                          accent={item.accent || color}
                          th={th}
                          dark={dark}
                        />
                      );
                    })
                  )}
                </g>
              );
            })}
        </svg>
      </div>
    </div>
  );
}

function dlBtn(th) {
  return {
    background: th.muted,
    border: `1px solid ${th.border}`,
    borderRadius: T.rsm,
    padding: "3px 9px",
    fontSize: 10,
    fontWeight: 600,
    color: th.textSec,
    cursor: "pointer",
    fontFamily: "monospace",
  };
}
